<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRuntimeConfig, navigateTo, useHead } from '#imports'
import { useScreen } from '@/composables/useScreen'
import { useI18n } from 'vue-i18n'
import { useAsyncData } from '#app'
import { VueperSlides, VueperSlide } from 'vueperslides'
import { obscureEmail, obscureString } from '@/helpers'

const axios = useAxios()
const route = useRoute()
const { isMobileView } = useScreen()
const { t } = useI18n()
const { user } = useSession()

const phoneVisible = ref(false)
const websiteVisible = ref(false)
const emailVisible = ref(false)
const showObscured = computed(() => !!route.query.show && !!user.value)
const loginModalVisible = ref(false)

const {
  data: firmPageData,
  status,
  error: mainError, // leaving for future improvements
  refresh: refreshData
} = useAsyncData(`firm-ssr-data-${route.params.slug}`, async () => {
  try {
    // Fetch main firm
    const firmRes = await axios.get(`firms/by-slug/${route.params.slug}`)
    const firmData = firmRes.data

    // Fetch lawyers & competing in parallel
    const [lawyersRes, competeRes] = await Promise.all([
      axios.get(`lawyers/by-firm/${firmData.id}`),
      axios.get('/competing-firms', { params: { firm_slug: firmData.slug } })
    ])

    // Compute price range text on SSR
    const { min_hourly_rate, max_hourly_rate } = firmData
    if (min_hourly_rate && max_hourly_rate) {
      if (min_hourly_rate === max_hourly_rate) {
        firmData.firmHourlyRateText = `${min_hourly_rate}€/${t('hour_abbreviation')}`
      } else {
        firmData.firmHourlyRateText = `${min_hourly_rate} - ${max_hourly_rate}€/${t('hour_abbreviation')}`
      }
    } else {
      firmData.firmHourlyRateText = `- €/${t('hour_abbreviation')}`
    }

    return {
      firm: firmData,
      lawyers: lawyersRes.data,
      competingFirmsList: competeRes.data
    }
  } catch (err) {
    console.error('SSR fetch failed:', err)
    return {
      firm: { firmHourlyRateText: `- €/${t('hour_abbreviation')}` },
      lawyers: [],
      competingFirmsList: []
    }
  }
})

const isLoading = computed(() => status.value === 'pending')
const firm = computed(() => firmPageData.value?.firm || {})
const lawyers = computed(() => firmPageData.value?.lawyers || [])
const competingFirmsList = computed(() => firmPageData.value?.competingFirmsList || [])

const parsedLanguages = computed(() => {
  const list = Array.isArray(lawyers.value) ? lawyers.value : []
  const languagesArray = list.flatMap(lawyer =>
    Object.values(lawyer?.languages ?? {})
  )
  const unique = [...new Set(languagesArray)]
  return unique.map(lang => `${lang} ${t('language_abbreviation')}.`).join(', ')
})

const chunkedLawyers = computed(() => {
  const chunkSize = 3
  const chunks = []
  for (let i = 0; i < lawyers.value.length; i += chunkSize) {
    chunks.push(lawyers.value.slice(i, i + chunkSize))
  }
  return chunks
})

const arrowShift = computed(() => 45 + (chunkedLawyers.value.length * 34 / 2))

const allProfessionStatuses = ['lawyer2', 'associate', 'lawyer2', 'expert_advisor']
function getProfessionStatusValue(professionStatus: any) {
  if (!professionStatus) return ''
  const key = parseInt(Object.keys(professionStatus)[0], 10)
  return allProfessionStatuses[key - 1] || ''
}

function showPhone () {
  if (!user.value)
    loginModalVisible.value = true
  else
    phoneVisible.value = true
}

function showWebsite () {
  if (!user.value)
    loginModalVisible.value = true
  else
    websiteVisible.value = true
}

function showEmail () {
  if (!user.value) 
    loginModalVisible.value = true
  else
    emailVisible.value = true
}

function navigateToLogin () {
  const redirectPath = btoa(`${route.fullPath}?show=true`)
  return navigateTo(`/${t('login_path')}?redirect=${redirectPath}`)
}

// ---- SEO improvements ----
// Dynamic <title> and <meta> description
const config = useRuntimeConfig()

const pageTitle = computed(() => {
  return firm.value?.name
    ? `${firm.value.name}`
    : 'Loading...'
})

const metaDescription = computed(() => {
  const about = firm.value?.about || ''
  return about.slice(0, 155) // or any logic you'd like
})

// Canonical URL
const canonicalLink = computed(() => {
  const baseUrl = config.public.baseURL.includes('localhost')
    ? 'https://geriteisininkai.lt'
    : config.public.baseURL
  return `${baseUrl}${route.fullPath}`
})

// JSON-LD structured data
const structuredData = computed(() => {
  if (!firm.value.id) return {}
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "name": firm.value?.name || '',
    "url": canonicalLink.value || '',
    "logo": firm.value?.logo || '',
    "address": firm.value?.address || '',
    "telephone": firm.value?.phone || '',
    "email": firm.value?.email || '',
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": firm.value?.average_rating || 0,
    }
  }
})

// Add to <head>
useHead({
  title: pageTitle,
  meta: [
    {
      name: 'description',
      content: metaDescription.value
    },
    // Example open graph tags
    {
      property: 'og:title',
      content: pageTitle.value
    },
    {
      property: 'og:description',
      content: metaDescription.value
    },
    {
      property: 'og:image',
      content: firm.value.logo || '@/assets/images/firm.png'
    }
  ],
  link: [
    {
      rel: 'canonical',
      href: canonicalLink.value
    }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: computed(() => JSON.stringify(structuredData.value))
    }
  ]
})
</script>

<template>
  <div v-if="!isLoading" class="font-redhat font-medium">
    <div class="container mt-8 text-sm lg:text-md xl:flex">
      <div class="md:flex md:justify-center">
        <div class="h-full w-full xs:h-[484px] xs:w-[423px] relative rounded-[40px] bg-white-500 select-none"> 
          <GTImage :src="firm.logo" alt="law firm logo" class="rounded-[20px] md:rounded-[40px] h-full w-full object-cover object-center">
            <template #fallback>
              <img src="/images/firm.svg" alt="Firm" class="translate-y-12 xl:translate-y-18 md:max-w-[333px] md:max-h-[333px] mx-auto object-cover object-center">
            </template>
          </GTImage>
          <div v-if="firm.logo">
            <div class="flex items-center justify-center">
              <div class="rounded-[28px] w-[95%] h-[48px] absolute bottom-3 z-10 text-white bg-lightBlue flex items-center justify-center">
                <rating-stars :value="firm.average_rating" :text-size="isMobileView? 16 : 18" class="*:mx-0.5"/>
              </div>
            </div>
          </div>
          <div class="bg-white-300 top-[88px] rounded-r-[12px] w-[88px] h-[52px] absolute z-10 text-darkBlue flex items-center justify-center p-3 text-sm">
            {{ $t('firm') }}
          </div>
        </div>
      </div>

      <div class="xl:ml-12 text-darkBlue w-full mr-auto">
        <div class="mt-8 lg:mt-[43px] flex items-center justify-center">
          <h1 class="text-30 lg:text-60 leading-tight text-darkBlue lg:mr-2">
            {{ firm.name }}
          </h1>
          <div class="h-[48px] rounded-[12px] min-w-[130px] ml-auto bg-lightBlue text-white text-md flex items-center justify-center px-4">
            {{ firm.firmHourlyRateText }}
          </div>
        </div>

        <div v-if="firm.address" class="mt-4 flex items-center">
          <IconLawyerAndFirmLocation class="text-lightBlue size-5 lg:size-6" />
          <div class="ml-3">
            {{ firm.address }}
          </div>
        </div>

        <div v-if="parsedLanguages" class="mt-4 flex items-center">
          <IconLawyerAndFirmGlobe class="text-lightBlue size-5 lg:size-6" />
          <div class="ml-3">
            {{ parsedLanguages }}
          </div>
        </div>

        <div v-if="firm.phone" class="mt-4 flex items-center h-6">
          <IconPhone class="text-lightBlue size-5 ml-1" />
          <a v-if="phoneVisible || showObscured" class="ml-3" :href="`tel:${firm.phone}`">
            {{ firm.phone }}
          </a>
          <button v-else class="ml-3 flex items-center" @click="showPhone">
            {{ obscureString(firm.phone) }}
            <ClientOnly>
              <el-tooltip placement="top" :content="$t('click_to_show')" effect="blue">
                <IconEyeShow class="ml-2 size-7 text-gray-500 hover:text-lightBlue focus:outline-none"/>
              </el-tooltip>
            </ClientOnly> 
          </button>        
        </div>

        <div v-if="firm.email" class="mt-4 flex items-center h-6">
          <IconEmail class="text-lightBlue size-5 ml-1" />
          <a v-if="emailVisible || showObscured" class="ml-3" :href="`mailto:${firm.email}`">
            {{ firm.email }}
          </a>
          <button v-else class="ml-3 flex items-center" @click="showEmail">
            {{ obscureEmail(firm.email) }}
            <ClientOnly>
              <el-tooltip placement="top" :content="$t('click_to_show')" effect="blue">
                <IconEyeShow class="ml-2 size-7 text-gray-500 hover:text-lightBlue focus:outline-none"/>
              </el-tooltip>
            </ClientOnly> 
          </button>        
        </div>

        <div v-if="firm.web_page" class="mt-4 flex items-center h-6">
          <IconWebsite class="text-lightBlue size-6 ml-0.5" />
          <a v-if="websiteVisible || showObscured" class="ml-3" :href="firm.web_page" target="_blank" rel="noopener noreferrer">
            {{ firm.web_page }}
          </a>
          <button v-else class="ml-3 flex items-center" @click="showWebsite">
            {{ obscureString(firm.web_page) }}
            <ClientOnly>
              <el-tooltip placement="top" :content="$t('click_to_show')" effect="blue">
                <IconEyeShow class="ml-2 size-7 text-gray-500 hover:text-lightBlue focus:outline-none"/>
              </el-tooltip>
            </ClientOnly>  
          </button>        
        </div>

        <GTModal :isOpen="loginModalVisible" :width="440" :closeIcon="true" @close="loginModalVisible = false">
          <template #header>
            <div class="flex justify-center mb-4">
              <IconLogin class="size-[50px]" />
            </div>
            {{ $t('need_to_login') }}
          </template>

          <template #content>
            <div class="flex justify-center my-6">
              <div class="text-center w-[330px]">
                {{ $t('need_to_login_description') }}
              </div>
            </div>
          </template>

          <template #footer>
            <div class="flex justify-center">
              <button class="w-[202px] h-12 text-white bg-darkBlue hover:bg-lightBlue rounded-full" @click="navigateToLogin">
                {{ t('login') }}
              </button>
            </div>
          </template>
        </GTModal>
      </div>
    </div>

    <div v-if="firm.about" class="container mt-18 lg:mt-20 text-white">
      <div class="bg-lightBlue p-10 pt-6 rounded-[40px]">
        <div class="font-redhat font-semibold text-lg lg:text-36">
          {{ $t('about_us') }}
        </div>
        <div class="mt-3 lg:mt-6 font-redhat">
          {{ firm.about || $t('no_description') }}
        </div>
      </div>
    </div>

    <div v-if="!isLoading && lawyers && lawyers.length" class="container mt-18 lg:mt-20 mb-18 lg:mt-20">
      <div v-if="isMobileView">
        <div class="text-2xlg mb-6 flex justify-center">
          {{ $t('team') }}
        </div>
        <vueper-slides
          class="no-shadow"
          fixed-height="350px"
          :arrows="true"
          bullets-outside
          :autoplay="false"
          :dragging-distance="10"
          :lazyLoadOnDrag="true"
        >
          <template #bullet="{ active }">
            <div class="relative w-[25px] h-[4px] -mx-[6px] top-4 bg-darkBlue" :class="{'opacity-55' : !active}" />
          </template>
          <template #arrow-left>
            <div class="relative top-[217px]" :style="{left: `-${arrowShift}px`}">
              <IconArrowFullLeft class="text-darkBlue" />
            </div>
          </template>
          <template #arrow-right>
            <div class="relative top-[217px]" :style="{right: `-${arrowShift}px`}">
              <IconArrowFullRight class="text-darkBlue" />
            </div>
          </template>
          <vueper-slide v-for="(chunk, index) in chunkedLawyers" :key="index">
            <template #content>
              <div class="flex justify-center">
                <div>
                  <div v-for="lawyer in chunk" :key="lawyer.id">
                    <NuxtLinkLocale :to="{ name: 'lawyer-slug', params: { slug: lawyer.slug } }">
                      <div class="flex items-center mt-3 hover:underline border-2 border-white hover:border-darkBlue hover:rounded-[23px]">
                        <el-avatar :size="100" :src="lawyer.photo" class="!rounded-[20px]">
                          <IconBlankUser alt="reviewer" class="scale-75" />
                        </el-avatar>
                        <div class="mx-4">
                          <div>
                            {{ lawyer.name }} {{ lawyer.lastname }}
                          </div>
                          <div v-if="getProfessionStatusValue(lawyer.lawyer_profession_status)">
                            {{ $t(getProfessionStatusValue(lawyer.lawyer_profession_status)) }}
                          </div>
                        </div>
                      </div>
                    </NuxtLinkLocale>
                  </div>
                </div>
              </div>
            </template>
          </vueper-slide>
        </vueper-slides>
      </div>

      <div v-else class="shadow-[0_0_13px_2px_#DADADA80] rounded-[20px]">
        <div class="mx-12 py-8">
          <div class="text-36">
            {{ $t('team') }}
          </div>
          <div class="mt-6 flex">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 w-full">
              <div v-for="lawyer in lawyers" :key="lawyer.id" class="py-2 bg-white">
                <NuxtLinkLocale :to="{ name: 'lawyer-slug', params: { slug: lawyer.slug } }">
                  <div class="flex items-center hover:underline border-2 border-white hover:border-darkBlue hover:rounded-[23px]">
                    <el-avatar :size="100" :src="lawyer.photo" class="!rounded-[20px]">
                      <IconBlankUser alt="reviewer" class="scale-75" />
                    </el-avatar>
                    <div class="ml-4">
                      <div>
                        {{ lawyer.name }} {{ lawyer.lastname }}
                      </div>
                      <div v-if="getProfessionStatusValue(lawyer.lawyer_profession_status)">
                        {{ $t(getProfessionStatusValue(lawyer.lawyer_profession_status)) }}
                      </div>
                    </div>
                  </div>
                </NuxtLinkLocale>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-center">
      <div v-if="!isLoading && competingFirmsList && competingFirmsList.length" class="mt-20 container">
        <div class="flex justify-center text-darkBlue font-redhat font-semibold text-36 lg:text-60">
          {{ $t('top_rated_firms') }}
        </div>
        <div class="flex justify-center text-darkBlue font-redhat font-medium text-md mt-4 mb-6">
          {{ $t('top_rated_firms_description') }}
        </div>
        <div class="flex items-center justify-center">
          <NuxtLinkLocale
            :to="{
              name: 'lawyers-specialty',
              params: {
                specialty: $t('advocates').toLowerCase()
              },
              query: {
                display: $t('firms').toLowerCase()
              }
            }"
            class="py-3 px-6 h-[48px] max-w-[202px] rounded-[9999px] mb-14 text-md text-white w-full bg-darkBlue inline-flex items-center justify-center"
          >
            {{ $t('search_firms') }}
          </NuxtLinkLocale>
        </div>
        <recommended-firms :recommended-firms-list="competingFirmsList"/>
      </div>
    </div>
  </div>
</template>

<style scoped>
.default-img {
  width: 423px;
  height: 423px;
  transform: translate(0.5%, 8%);
}

:deep(.vueperslides__arrow) {
  color: #0E1535;
  opacity: 1;
}

:deep(.vueperslides__arrow) {
  color: #0E1535;
}

:deep(.vueperslides__arrow) {
  opacity: 1;
}

:deep(.vueperslides__arrow--prev) {
  left: 50%;
}

:deep(.vueperslides__arrow--next) {
  right: 50%;
}
</style>
